import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import McmvRegistrationForm from '../McmvRegistrationForm';
import '../McmvForm.css';
import '../Apoio/Dashboard.css';

interface Registration {
  id: number;
  numero_inscricao: string;
  user: {
    id: number;
    name: string;
    email: string;
    cpf: string;
  };
  status: 'pendente' | 'aprovado' | 'reprovado' | 'em_analise';
  created_at: string;
  updated_at: string;
}

const AdminDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingRegistration, setEditingRegistration] = useState<Registration | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    pendente: 0,
    em_analise: 0,
    aprovado: 0,
    reprovado: 0
  });

  useEffect(() => {
    if (activeSection === 'lista') {
      fetchRegistrations();
    } else if (activeSection === 'dashboard') {
      fetchStats();
    }
  }, [activeSection, currentPage, searchTerm, statusFilter]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/registrations/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
    }
  };

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/registrations', {
        params: {
          page: currentPage,
          search: searchTerm,
          status: statusFilter,
          per_page: 10
        }
      });
      setRegistrations(response.data.data);
      setTotalPages(response.data.last_page);
    } catch (error) {
      console.error('Erro ao buscar inscrições:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (registrationId: number, newStatus: string) => {
    try {
      await api.put(`/admin/registrations/${registrationId}/status`, {
        status: newStatus
      });
      fetchRegistrations();
      if (activeSection === 'dashboard') {
        fetchStats();
      }
    } catch (error) {
      console.error('Erro ao alterar status:', error);
    }
  };

  const handleDelete = async (registrationId: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta inscrição?')) {
      try {
        await api.delete(`/admin/registrations/${registrationId}`);
        fetchRegistrations();
        if (activeSection === 'dashboard') {
          fetchStats();
        }
      } catch (error) {
        console.error('Erro ao excluir inscrição:', error);
      }
    }
  };

  const renderDashboard = () => (
    <div className="dashboard-section">
      <h2>Painel Administrativo</h2>
      <div className="stats-grid">
        <div className="stat-card total">
          <h3>Total de Inscrições</h3>
          <p className="stat-number">{stats.total}</p>
        </div>
        <div className="stat-card pendente">
          <h3>Pendentes</h3>
          <p className="stat-number">{stats.pendente}</p>
        </div>
        <div className="stat-card em-analise">
          <h3>Em Análise</h3>
          <p className="stat-number">{stats.em_analise}</p>
        </div>
        <div className="stat-card aprovado">
          <h3>Aprovadas</h3>
          <p className="stat-number">{stats.aprovado}</p>
        </div>
        <div className="stat-card reprovado">
          <h3>Reprovadas</h3>
          <p className="stat-number">{stats.reprovado}</p>
        </div>
      </div>
      
       
    </div>
  );

  const renderRegistrationsList = () => (
    <div className="registrations-section">
      <div className="section-header">
        <h2>Gerenciar Inscrições</h2>
        <div className="filters">
          <input
            type="text"
            placeholder="Buscar por nome, CPF, email ou número de inscrição..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-filter"
          >
            <option value="">Todos os status</option>
            <option value="pendente">Pendente</option>
            <option value="em_analise">Em Análise</option>
            <option value="aprovado">Aprovado</option>
            <option value="reprovado">Reprovado</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Carregando...</div>
      ) : (
        <>
          <div className="registrations-table">
            <table>
              <thead>
                <tr>
                  <th>Nº Inscrição</th>
                  <th>Nome</th>
                  <th>CPF</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Data</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {registrations.map((registration) => (
                  <tr key={registration.id}>
                    <td className="numero-inscricao">{registration.numero_inscricao || 'N/A'}</td>
                    <td>{registration.user.name}</td>
                    <td>{registration.user.cpf}</td>
                    <td>{registration.user.email}</td>
                    <td>
                      <select
                        value={registration.status}
                        onChange={(e) => handleStatusChange(registration.id, e.target.value)}
                        className={`status-select status-${registration.status}`}
                      >
                        <option value="pendente">Pendente</option>
                        <option value="em_analise">Em Análise</option>
                        <option value="aprovado">Aprovado</option>
                        <option value="reprovado">Reprovado</option>
                      </select>
                    </td>
                    <td>{new Date(registration.created_at).toLocaleDateString('pt-BR')}</td>
                    <td>
                      <button
                        onClick={() => {
                          setEditingRegistration(registration);
                          setActiveSection('editar');
                        }}
                        className="btn btn-sm btn-primary"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(registration.id)}
                        className="btn btn-sm btn-danger"
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              >
                {page}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const renderCreateForm = () => (
    <div className="create-section">
    </div>
  );

  const renderEditForm = () => (
    <div className="edit-section">
      <h2>Editar Inscrição</h2>
      <p>Editando inscrição de: {editingRegistration?.user.name}</p>
      <McmvRegistrationForm 
        editData={editingRegistration}
        onSuccess={() => {
          setActiveSection('lista');
          setEditingRegistration(null);
        }}
      />
    </div>
  );

  const renderReports = () => (
    <div className="reports-section">
      <h2>Relatórios</h2>
      <div className="report-options">
        <div className="report-card">
          <h3>Relatório Geral</h3>
          <p>Exportar todas as inscrições com seus status</p>
          <button
            className="btn btn-primary"
            onClick={async () => {
              try {
                const res = await api.get('/admin/pre-registrations/export/csv', {
                  responseType: 'blob'
                });
                const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `inscricoes_mcmv_${new Date().toISOString().slice(0,19)}.csv`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
              } catch (error) {
                console.error('Erro ao exportar CSV:', error);
                alert('Falha ao exportar CSV. Verifique sua conexão e tente novamente.');
              }
            }}
          >
            Exportar CSV
          </button>
        </div> 
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return renderDashboard();
      case 'lista':
        return renderRegistrationsList();
      case 'criar':
        return renderCreateForm();
      case 'editar':
        return renderEditForm();
      case 'relatorios':
        return renderReports();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="apoio-dashboard admin-dashboard">
      <div className="dashboard-header">
        <h1>Painel Administrativo MCMV</h1>
        <div className="user-info">
          <span>Bem-vindo(a), {user?.name}</span>
          <button onClick={logout} className="btn btn-secondary">
            Sair
          </button>
        </div>
      </div>

      <div className="dashboard-nav">
        <button
          onClick={() => setActiveSection('dashboard')}
          className={`nav-btn ${activeSection === 'dashboard' ? 'active' : ''}`}
        >
          🏠 Dashboard
        </button>
        
         
        <button
          onClick={() => setActiveSection('relatorios')}
          className={`nav-btn ${activeSection === 'relatorios' ? 'active' : ''}`}
        >
          📊 Relatórios
        </button>
      </div>

      <div className="dashboard-content">
        {renderContent()}
      </div>
    </div>
  );
};

export default AdminDashboard;